import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormField, WarehouseSelect } from "@/components/stock/FormField";
import { toast } from "sonner";

// Delete a PO receipt row: only this item, or the whole receipt document.
export function ReceiptDeleteDialog({ tx, onClose, onDone }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async (url) => {
    if (busy) return;
    setBusy(true);
    try { await api.delete(url); toast.success(t("deleted")); onClose(); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="receipt-delete-dialog">
        <DialogHeader><DialogTitle>{t("delete_receipt")}</DialogTitle></DialogHeader>
        {tx && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">{t("delete_receipt_hint")}</p>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 font-mono text-xs">
              {tx.doc_no} · {tx.ref_no}<br />{tx.item_code} · +{fmtQty(tx.qty)} {tx.unit}
            </div>
            <Button variant="outline" disabled={busy} className="w-full justify-start text-red-700 border-red-200" onClick={() => run(`/po-receipts/${tx.doc_no}/txn/${tx.id}`)} data-testid="receipt-delete-line-btn">
              {t("delete_receipt_line")} ({tx.item_code})
            </Button>
            <Button variant="outline" disabled={busy} className="w-full justify-start text-red-700 border-red-200" onClick={() => run(`/po-receipts/${tx.doc_no}`)} data-testid="receipt-delete-doc-btn">
              {t("delete_receipt_doc")} ({tx.doc_no})
            </Button>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="receipt-delete-cancel-btn">{t("cancel")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Correct a goods receipt posted against a PO: qty, date, warehouse and note; PO received qty is recalculated.
export function POReceiptEditDialog({ docNo, onClose, onSaved }) {
  const { t } = useLang();
  const [f, setF] = useState(null);
  useEffect(() => {
    setF(null);
    if (docNo) api.get(`/po-receipts/${docNo}`).then((r) => setF(r.data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [docNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = () => { onClose(); onSaved(); };
  const save = async () => {
    try {
      await api.put(`/po-receipts/${docNo}`, { date: f.date, warehouse: f.warehouse, note: f.note, lines: f.lines.map((l) => ({ line_id: l.line_id, qty: Number(l.receipt_qty || 0) })) });
      toast.success(t("saved"));
      done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancelReceipt = async () => {
    if (!window.confirm(t("cancel_receipt_confirm"))) return;
    try { await api.delete(`/po-receipts/${docNo}`); toast.success(t("saved")); done(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setLine = (i, v) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, receipt_qty: v } : l)) });

  return (
    <Dialog open={!!docNo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-receipt-edit-dialog">
        <DialogHeader><DialogTitle>{t("edit_receipt")} · {f?.po_number} <span className="font-mono text-xs text-slate-500">{docNo}</span></DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label={t("date")}><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="receipt-date-input" /></FormField>
              <FormField label={t("warehouse")}><WarehouseSelect value={f.warehouse} onChange={(v) => setF({ ...f, warehouse: v })} availableOnly testid="receipt-warehouse-select" /></FormField>
              <FormField label={t("note")}><Input value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="receipt-note-input" /></FormField>
            </div>
            <table className="min-w-full text-sm border border-slate-200" data-testid="receipt-lines-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="text-left px-3 py-2">{t("item")}</th><th className="text-right px-3 py-2">{t("ordered")}</th><th className="text-right px-3 py-2">{t("received")}</th><th className="text-right px-3 py-2">{t("receipt_qty")}</th></tr>
              </thead>
              <tbody>
                {f.lines.map((l, i) => (
                  <tr key={l.line_id} className="border-t border-slate-100">
                    <td className="px-3 py-2"><div className="font-mono text-xs">{l.code}</div><div className="text-xs text-slate-500">{l.name}</div></td>
                    <td className="px-3 py-2 text-right font-mono">{fmtQty(l.qty)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtQty(l.received_qty)}</td>
                    <td className="px-3 py-2 text-right">
                      <Input type="number" min="0" max={l.max_qty} value={l.receipt_qty} onChange={(e) => setLine(i, e.target.value)} className="w-28 ml-auto font-mono text-right" data-testid={`receipt-qty-${i}`} />
                      <div className="text-[11px] text-slate-400 mt-0.5">{t("max_qty")} {fmtQty(l.max_qty)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" className="text-red-700 border-red-200" onClick={cancelReceipt} disabled={!f} data-testid="receipt-cancel-receipt-btn">{t("cancel_receipt")}</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button onClick={save} disabled={!f} className="bg-slate-900 hover:bg-slate-800" data-testid="receipt-save-btn">{t("save")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
